import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { mockCategories } from "@/mocks/products";

interface CategoryCount {
  name: string;
  count: number;
}

export default function CategorySection() {
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("products")
        .select("category")
        .neq("category", "")
        .order("category", { ascending: true });

      if (error || !data) {
        const fallback = mockCategories.map((c) => ({
          name: c.name,
          count: c.count,
        }));
        setCategories(fallback);
        setLoading(false);
        return;
      }

      const counts: Record<string, number> = {};
      data.forEach((item: { category: string }) => {
        counts[item.category] = (counts[item.category] || 0) + 1;
      });

      const mapped = mockCategories.map((mock) => ({
        name: mock.name,
        count: counts[mock.name] || 0,
      }));

      setCategories(mapped);
      setLoading(false);
    };

    fetchCategoryCounts();
  }, []);

  const displayCategories = loading
    ? mockCategories.map((c) => ({ name: c.name, count: c.count }))
    : categories;

  return (
    <section className="py-14 px-6 lg:px-12 bg-stone-50">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">熱門跨境品類</h2>
            <p className="text-gray-500 text-sm mt-1">精選優質貨源，覆蓋多個熱門品類</p>
          </div>
          <Link
            to="/products"
            className="hidden sm:flex items-center gap-1 text-amber-600 text-sm font-medium hover:text-amber-700 cursor-pointer whitespace-nowrap"
          >
            查看全部
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {displayCategories.map((cat) => (
          <Link
            key={cat.name}
            to={`/products?category=${encodeURIComponent(cat.name)}`}
            className="group relative rounded-2xl overflow-hidden cursor-pointer"
          >
            <div className="aspect-[4/3]">
              <img
                src={mockCategories.find((m) => m.name === cat.name)?.image || ""}
                alt={cat.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <div className="text-white font-semibold text-sm">{cat.name}</div>
              <div className="text-white/70 text-xs mt-0.5">{cat.count} 商品</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}